import { useCallback, useEffect, useRef, useState } from 'react';

import { env } from '../lib/env.js';
import { authApi } from '../api/auth.js';
import { SAMPLE_RATE, floatTo16BitPCM, int16ToBase64 } from '../lib/audio/pcm.js';
import { VoicePlayer } from '../lib/audio/player.js';

// status: idle | connecting | live | limited | error | closed
// The orb shows finer states (listening/speaking) from `speaking`+`userSpeaking`.

// Broker refusal codes that mean a daily/usage limit was hit (or the audience is
// blocked). These map to the dedicated "Limite raggiunto" orb state, distinct
// from transient realtime errors.
const LIMIT_CODES = new Set([
  'audience_blocked',
  'audience_budget_exceeded',
  'store_budget_exceeded',
  'budget_exceeded'
]);

function buildWsUrl(params) {
  const base = env.apiBase || '/api/v1';
  const httpBase = /^https?:\/\//.test(base) ? base : window.location.origin + base;
  const url = new URL(`${httpBase}/voice/session`);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v) url.searchParams.set(k, v);
  });
  return url.toString();
}

/**
 * Realtime voice session against the server broker. Handles microphone
 * capture (PCM16 @ 24 kHz), audio playback, live transcripts, and status.
 */
export function useRealtimeVoice() {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);       // assistant audio playing
  const [userSpeaking, setUserSpeaking] = useState(false); // server VAD detected speech
  const [transcript, setTranscript] = useState([]);       // [{ id, role, text, final }]
  const [meta, setMeta] = useState(null);                 // broker.ready payload
  const [conversationId, setConversationId] = useState(null); // persisted thread id
  const [level, setLevel] = useState(0);                  // combined voice amplitude 0..1

  const wsRef = useRef(null);
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const nodeRef = useRef(null);
  const playerRef = useRef(null);
  const mutedRef = useRef(false);
  const asstIdRef = useRef(null);
  const userIdRef = useRef(null);
  // Amplitude metering: an analyser on the mic input plus the player's output
  // analyser drive a single `level` the orb pulses with.
  const inAnalyserRef = useRef(null);
  const inBufRef = useRef(null);
  const rafRef = useRef(null);
  // Set while a stop() / cleanup() is intentional, so the WebSocket's error and
  // close events are not surfaced as a failure to the user.
  const closingRef = useRef(false);
  // Bumped on every start/stop. Used to invalidate a start whose async setup
  // (getUserMedia, audioWorklet) resolves after the user already stopped, so a
  // late microphone stream cannot leak and keep the mic indicator lit.
  const genRef = useRef(0);
  const errorTimerRef = useRef(null);

  const appendAssistantDelta = useCallback((delta) => {
    setTranscript((prev) => prev.map((m) => (
      m.id === asstIdRef.current ? { ...m, text: m.text + delta } : m
    )));
  }, []);

  const finalizeAssistant = useCallback((fullText) => {
    setTranscript((prev) => prev.map((m) => (
      m.id === asstIdRef.current
        ? { ...m, text: typeof fullText === 'string' && fullText ? fullText : m.text, final: true }
        : m
    )));
  }, []);

  const cleanup = useCallback(() => {
    // Invalidate any start() still resolving so its late stream is discarded.
    genRef.current += 1;
    if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    inAnalyserRef.current = null;
    inBufRef.current = null;
    setLevel(0);
    if (nodeRef.current) { try { nodeRef.current.port.onmessage = null; nodeRef.current.disconnect(); } catch { /* */ } nodeRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (ctxRef.current) { try { ctxRef.current.close(); } catch { /* */ } ctxRef.current = null; }
    if (playerRef.current) { playerRef.current.close(); playerRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch { /* */ } wsRef.current = null; }
  }, []);

  const stop = useCallback(() => {
    // Mark the teardown intentional so the socket's error/close events do not
    // surface as a failure: the user closed the call on purpose.
    closingRef.current = true;
    cleanup();
    setStatus('closed');
    setSpeaking(false);
    setUserSpeaking(false);
    // Clear the live transcript so the panel is empty and ready for a new
    // session. The turn is already persisted server side and shows up in the
    // conversation list, so nothing is lost. `conversationId` is kept so the
    // caller can still refresh that list on close.
    setTranscript([]);
    setError(null);
    userIdRef.current = null;
    asstIdRef.current = null;
  }, [cleanup]);

  const handleEvent = useCallback((evt) => {
    switch (evt.type) {
      case 'broker.ready':
        setMeta(evt);
        setStatus('live');
        break;
      case 'broker.error': {
        // Fatal: the broker is tearing the session down.
        setError(evt.message || 'Errore di connessione vocale');
        setStatus('error');
        break;
      }
      case 'error': {
        // Close the assistant bubble this failed response had opened so an
        // empty turn does not linger in the transcript.
        setTranscript((prev) => prev.map((m) => (
          m.id === asstIdRef.current && !m.final ? { ...m, final: true } : m
        )));
        if (evt.error) {
          // OpenAI Realtime `error` events (they carry an `error` object) are
          // mostly transient (e.g. a barge-in race producing "conversation
          // already has an active response"). The session keeps working, so
          // surface them softly and auto-clear so they do not linger.
          const detail = evt.error.message || evt.error.code || 'Errore temporaneo';
          const benign = evt.error.code === 'conversation_already_has_active_response'
            || /active response/i.test(detail);
          if (!benign) {
            setError(detail);
            if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
            errorTimerRef.current = setTimeout(() => setError(null), 5000);
          }
          break;
        }
        // Broker-originated terminal error (no realtime `error` object): the
        // session is being refused/closed. Release the mic and show a
        // persistent state. Daily-limit/block codes get the dedicated state.
        cleanup();
        if (errorTimerRef.current) { clearTimeout(errorTimerRef.current); errorTimerRef.current = null; }
        setError(evt.message || 'Connessione vocale fallita');
        setStatus(LIMIT_CODES.has(evt.code) ? 'limited' : 'error');
        setSpeaking(false);
        setUserSpeaking(false);
        break;
      }
      case 'broker.notice':
        // Notices precede a server-initiated close. Reaching the per-session
        // time cap maps to the dedicated limit state; idle just closes.
        setError(evt.message || null);
        if (evt.code === 'max_duration') setStatus('limited');
        break;
      case 'broker.conversation':
        // The broker persisted this voice session as a conversation.
        if (evt.conversationId) setConversationId(evt.conversationId);
        break;
      case 'input_audio_buffer.speech_started':
        setUserSpeaking(true);
        // Barge-in: stop assistant playback when the user starts talking.
        if (playerRef.current) playerRef.current.clear();
        // Open the user's bubble as soon as speech is detected. This precedes
        // the assistant bubble (response.created) so the turn order is correct
        // even when the transcription completes late.
        userIdRef.current = `u-${Date.now()}`;
        setTranscript((prev) => [...prev, { id: userIdRef.current, role: 'user', text: '', final: false }]);
        break;
      case 'input_audio_buffer.speech_stopped':
        setUserSpeaking(false);
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (evt.transcript) {
          setTranscript((prev) => {
            // Fill the placeholder opened at speech start.
            if (userIdRef.current && prev.some((m) => m.id === userIdRef.current)) {
              return prev.map((m) => (m.id === userIdRef.current ? { ...m, text: evt.transcript, final: true } : m));
            }
            return [...prev, { id: `u-${prev.length}-${Date.now()}`, role: 'user', text: evt.transcript, final: true }];
          });
        }
        break;
      case 'response.created':
        // Open a new assistant bubble to receive transcript deltas.
        asstIdRef.current = `a-${Date.now()}`;
        setTranscript((prev) => [...prev, { id: asstIdRef.current, role: 'assistant', text: '', final: false }]);
        break;
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        if (evt.delta) appendAssistantDelta(evt.delta);
        break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done':
        finalizeAssistant(evt.transcript);
        break;
      case 'response.output_audio.delta':
      case 'response.audio.delta':
        if (evt.delta && playerRef.current) playerRef.current.enqueue(evt.delta);
        break;
      case 'response.done':
        // Close the current assistant bubble. A tool-only response carries no
        // transcript, so its empty bubble would otherwise linger (it is dropped
        // by the transcript filter once finalized).
        setTranscript((prev) => prev.map((m) => (
          m.id === asstIdRef.current && !m.final ? { ...m, final: true } : m
        )));
        break;
      default:
        break;
    }
  }, [appendAssistantDelta, finalizeAssistant, cleanup]);

  const start = useCallback(async (params) => {
    // Tear down any prior graph and claim a fresh generation token.
    cleanup();
    closingRef.current = false;
    const myGen = (genRef.current += 1);
    const stale = () => myGen !== genRef.current;
    setError(null);
    setTranscript([]);
    setConversationId(null);
    userIdRef.current = null;
    asstIdRef.current = null;
    setStatus('connecting');
    mutedRef.current = false;
    setMuted(false);

    try {
      // 1. Microphone + 24 kHz capture graph.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true }
      });
      // The user may have stopped while the permission prompt was open: discard
      // the late stream so the microphone is released immediately.
      if (stale()) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;

      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx({ sampleRate: SAMPLE_RATE });
      ctxRef.current = ctx;
      await ctx.audioWorklet.addModule('/worklets/recorder-worklet.js');
      if (stale()) { cleanup(); return; }

      const player = new VoicePlayer(setSpeaking);
      playerRef.current = player;

      // 2. Refresh the access token right before the handshake. The WS upgrade
      // authenticates with the httpOnly access cookie, but unlike HTTP requests
      // it has no silent-refresh path: once the 15 minute access cookie lapses
      // the handshake fails with "Missing bearer token" even though the session
      // is still valid. Rotating first guarantees a fresh cookie at connect.
      try { await authApi.refresh(); } catch { /* fall through: the WS will surface a clearer auth error */ }
      if (stale()) { cleanup(); return; }

      // 3. WebSocket to the broker (cookies sent automatically, same origin).
      const ws = new WebSocket(buildWsUrl(params));
      wsRef.current = ws;

      const source = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, 'recorder-processor');
      nodeRef.current = node;
      node.port.onmessage = (e) => {
        if (mutedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
        const pcm = floatTo16BitPCM(e.data);
        wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: int16ToBase64(pcm) }));
      };
      source.connect(node);
      // Keep the node pulling without routing mic to speakers.
      node.connect(ctx.destination);

      // Amplitude metering: analyse the mic input and combine it with the
      // player's output level so the orb pulses with whoever is speaking.
      const inAnalyser = ctx.createAnalyser();
      inAnalyser.fftSize = 256;
      source.connect(inAnalyser);
      inAnalyserRef.current = inAnalyser;
      inBufRef.current = new Uint8Array(inAnalyser.fftSize);
      const meter = () => {
        if (stale()) return;
        let input = 0;
        const an = inAnalyserRef.current;
        const buf = inBufRef.current;
        if (an && buf && !mutedRef.current) {
          an.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; sum += v * v; }
          input = Math.sqrt(sum / buf.length);
        }
        const output = playerRef.current ? playerRef.current.getLevel() : 0;
        // Emphasise the active speaker; clamp to a comfortable range.
        const lvl = Math.min(1, Math.max(input, output) * 3.2);
        setLevel(lvl);
        rafRef.current = requestAnimationFrame(meter);
      };
      rafRef.current = requestAnimationFrame(meter);

      ws.onmessage = (e) => {
        let evt;
        try { evt = JSON.parse(e.data); } catch { return; }
        handleEvent(evt);
      };
      ws.onerror = () => {
        // A socket error fired by our own teardown (the user pressed end) is
        // expected: do not show it as a failure.
        if (closingRef.current) return;
        setError('Connessione vocale fallita');
        setStatus('error');
      };
      ws.onclose = () => {
        // Release the capture graph so a server-initiated close (idle, max
        // duration, budget) does not leave the microphone live. Preserve a
        // terminal error/limit state set by the error frame.
        cleanup();
        setStatus((s) => (s === 'error' || s === 'limited' ? s : 'closed'));
        setSpeaking(false);
        setUserSpeaking(false);
      };
    } catch (err) {
      setError(err && err.name === 'NotAllowedError' ? 'Permesso microfono negato' : (err.message || 'Avvio voce fallito'));
      setStatus('error');
      cleanup();
    }
  }, [handleEvent, cleanup]);

  const toggleMute = useCallback(() => {
    mutedRef.current = !mutedRef.current;
    setMuted(mutedRef.current);
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  return { status, error, transcript, meta, conversationId, speaking, userSpeaking, muted, level, start, stop, toggleMute };
}
